/**
 * Opportunity Candidates (Client Portal) — port pixel-perfect del handoff
 * `scouting-wizard-client-portal/project/Opportunity Candidates.html` (SW-144).
 *
 * Estructura:
 *   1. Breadcrumb bar con acciones Follow / Share / Export.
 *   2. Hero opportunity (eyebrow + título + meta) + stats card 4-up
 *      (Presented · New · Seen · Not for now).
 *   3. Tabs nav 4: Candidates · Overview · Comments · Activity (Candidates por defecto).
 *   4. Toolbar con filter chips por status (All/New/Seen/Not for now), search input y
 *      Expand all (toggle de la lista de tarjetas).
 *   5. Lista de candidate cards 3-col (avatar gradient · body · side) con status pill,
 *      Level/Rate, y acciones Dismiss/Restore/Share/View.
 *
 * Datos reales (BE):
 *   - Lista de talents via `useShortlistTalents` (Project.PublicKey path).
 *   - Status por talent del `myAction` que el BE devuelve EN el list response
 *     (SW-145 elimina el N+1: antes se hacía GET /actions per tarjeta).
 *   - Dismiss/Restore mutation via `useSetTalentAction` (mapea a not_interested /
 *     interested respectivamente). Tras success invalida el list query → refetch
 *     con el nuevo `myAction`.
 *
 * Placeholders gracefules (no BE aún):
 *   - Hero meta: solo lo disponible (project name, candidate count). Hires needed,
 *     región, budget, hiring manager → fuera de scope hasta endpoint enriquecido.
 *   - Bullets por tarjeta: no hay BE, omitimos esa sección (no inventamos copy).
 *   - Rate por tarjeta: TalentListItem no incluye hourlyRate (vive en el detail),
 *     mostramos "—". Defer hasta endpoint enriquecido.
 *   - Domain / Companies chips por tarjeta: igual, omitimos hasta endpoint enriquecido.
 *   - "Seen" como estado: no hay tracking de viewed. La tab "Seen" filtra por
 *     no-interested + no-interested-status (= sin acción registrada) → en MVP el chip
 *     "Seen" no se usa (chips son All / New / Not for now / Interested).
 *   - Category grouping (Mobile / Full-Stack / QA): no hay categorías en BE,
 *     renderizamos todo en un solo grupo "All candidates".
 *   - Overview / Comments / Activity tabs: empty state "Coming soon".
 *
 * Avatar variant (av-1..av-7): derivada por hash determinista del id (Guid). Los
 * gradients viven inline porque son específicos de este componente.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent } from 'react';
import { Alert, Box, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { tokens } from '../../theme/tokens';
import { ApiError } from '@sw/api-client';
import { useNotify } from '../../lib/notify';
import { useShortlists } from './useShortlists';
import { useShortlistTalents } from './useShortlistTalents';
import { useSetTalentAction } from './useTalentActions';
import type { TalentListItem } from './talentsApi';
import type { TalentActionType } from './talentActionsApi';
import { computeInitials, formatLocation, hashIndex } from './_helpers';

// ============================================================================
// CONSTANTS / TYPES
// ============================================================================

// SW-145: el N+1 de `useTalentActions` per-card fue eliminado — el BE incluye
// `myAction` en el list response. Bump del cap a 50 ahora que cada talent es
// 0 fetches adicionales (el chip de status se deriva del item directo).
const PAGE_SIZE = 50;

// Guid v7 estándar (acepta también v4 para rows backfilleados pre-SW-407).
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type CandidateStatus = 'new' | 'seen' | 'dismissed' | 'interested';
type FilterKey = 'all' | 'new' | 'seen' | 'dismissed';
type TabKey = 'candidates' | 'overview' | 'comments' | 'activity';

// Avatar gradients 1..7 (portados del handoff). Inline para mantener un único archivo.
const AVATAR_GRADIENTS = [
  'linear-gradient(140deg, #1f6feb, #0a4dbf)',
  'linear-gradient(140deg, #c2410c, #7c2d12)',
  'linear-gradient(140deg, #15803d, #0f5d2e)',
  'linear-gradient(140deg, #7c3aed, #4c1d95)',
  'linear-gradient(140deg, #0f766e, #134e4a)',
  'linear-gradient(140deg, #be185d, #831843)',
  'linear-gradient(140deg, #4338ca, #1e1b4b)',
] as const;

// ============================================================================
// PAGE
// ============================================================================

export default function OpportunityDetailPage() {
  const { t } = useTranslation();
  const params = useParams<{ shortlistId: string }>();
  const shortlistPublicKey = params.shortlistId ?? '';
  const isValidId = GUID_RE.test(shortlistPublicKey);

  // Cache hit del list query para el name; si el user entró por deep link sin pasar
  // por /opportunities, este query corre on-mount y eventualmente popula el nombre.
  const shortlistsQuery = useShortlists();
  const shortlist = shortlistsQuery.data?.items.find((s) => s.id === shortlistPublicKey);
  const shortlistName = shortlist?.name;
  const shortlistStatus = shortlist?.status;

  const [pageNumber, setPageNumber] = useState(1);
  const talentsQuery = useShortlistTalents({
    shortlistPublicKey: isValidId ? shortlistPublicKey : '00000000-0000-0000-0000-000000000000',
    pageNumber,
    pageSize: PAGE_SIZE,
  });

  // 404 backend (shortlist no compartido / revocado / Project Draft|Archived).
  const isNotFound =
    !isValidId ||
    (talentsQuery.error instanceof ApiError && talentsQuery.error.status === 404);

  return (
    <Box
      data-testid="opportunity-detail-page"
      sx={{ display: 'flex', flexDirection: 'column' }}
    >
      <BreadcrumbBar />

      {isNotFound ? (
        <NotFoundState />
      ) : (
        <OpportunityBody
          shortlistPublicKey={shortlistPublicKey}
          shortlistName={shortlistName ?? null}
          shortlistStatus={shortlistStatus ?? null}
          isPending={talentsQuery.isPending}
          isSuccess={talentsQuery.isSuccess}
          items={talentsQuery.data?.items ?? []}
          totalCount={talentsQuery.data?.totalCount ?? 0}
          pageNumber={pageNumber}
          isFetching={talentsQuery.isFetching}
          onPageChange={setPageNumber}
        />
      )}

      {/* Back link mobile-friendly debajo del scroll, manteniendo el data-testid
          que ya consumen los tests existentes (back link discoverable). */}
      <Box
        component={Link}
        to="/opportunities"
        data-testid="opportunity-detail-back"
        sx={{
          mt: '32px',
          fontFamily: tokens.font.mono,
          fontSize: 11,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: tokens.color.muted,
          textDecoration: 'none',
          alignSelf: 'flex-start',
          '&:hover': { color: tokens.color.ink },
        }}
      >
        {t('opportunities.detail.backToList')}
      </Box>
    </Box>
  );
}

// ============================================================================
// BREADCRUMB BAR
// ============================================================================

function BreadcrumbBar() {
  const { t } = useTranslation();
  const notify = useNotify();
  const [isFollowing, setIsFollowing] = useState(false);

  const handleFollow = () => {
    // No hay BE para follow toggle aún; mantenemos el state local como afford UX y
    // notificamos al usuario que la persistencia llegará pronto.
    setIsFollowing((prev) => !prev);
    notify.info(t('opportunities.detail.actions.followSoon'));
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      notify.success(t('opportunities.detail.actions.shareCopied'));
    } catch {
      notify.error(t('opportunities.detail.actions.shareError'));
    }
  };

  const handleExport = () => {
    notify.info(t('opportunities.detail.actions.exportSoon'));
  };

  return (
    <Box
      component="nav"
      aria-label="Breadcrumb"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
        padding: '14px 0 18px',
        color: tokens.color.muted,
        fontSize: 13,
        borderBottom: `1px solid ${tokens.color.line}`,
        mb: '24px',
      }}
    >
      <Box
        component={Link}
        to="/opportunities"
        sx={{
          color: tokens.color.muted,
          textDecoration: 'none',
          padding: '4px 8px',
          borderRadius: '6px',
          '&:hover': { color: tokens.color.ink, background: tokens.color.surface2 },
        }}
      >
        {t('opportunities.detail.breadcrumb.opportunities')}
      </Box>
      <Box component="span" sx={{ flex: 1 }} />
      <IconButton
        data-testid="opportunity-action-follow"
        onClick={handleFollow}
        active={isFollowing}
        label={isFollowing
          ? t('opportunities.detail.actions.following')
          : t('opportunities.detail.actions.follow')}
        icon={
          <svg viewBox="0 0 16 16" width="14" height="14" fill={isFollowing ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.6" aria-hidden>
            <path d="M4 2.5h8v11l-4-2.5-4 2.5v-11z" />
          </svg>
        }
      />
      <IconButton
        data-testid="opportunity-action-share"
        onClick={handleShare}
        label={t('opportunities.detail.actions.share')}
        icon={
          <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
            <path d="M6 9.5l4-3M6 6.5l4 3M3.5 8a2 2 0 100-4 2 2 0 000 4zm9-3a2 2 0 100-4 2 2 0 000 4zm0 8a2 2 0 100-4 2 2 0 000 4z" />
          </svg>
        }
      />
      <IconButton
        data-testid="opportunity-action-export"
        onClick={handleExport}
        label={t('opportunities.detail.actions.export')}
        icon={
          <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
            <path d="M8 2v9m0 0L4.5 7.5M8 11l3.5-3.5M3 13.5h10" />
          </svg>
        }
      />
    </Box>
  );
}

function IconButton({
  icon,
  label,
  onClick,
  active,
  ...rest
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  active?: boolean;
  'data-testid'?: string;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      data-testid={rest['data-testid']}
      sx={{
        height: 34,
        padding: '0 12px',
        gap: '6px',
        display: 'inline-flex',
        alignItems: 'center',
        background: active ? tokens.color.ink : tokens.color.surface,
        border: `1px solid ${active ? tokens.color.ink : tokens.color.line}`,
        borderRadius: '10px',
        color: active ? tokens.color.accentInk : tokens.color.ink,
        fontSize: 13,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'border-color .15s ease, background .15s ease, color .15s ease',
        fontFamily: 'inherit',
        '&:hover': { borderColor: tokens.color.ink },
      }}
    >
      {icon}
      {label}
    </Box>
  );
}

// ============================================================================
// OPPORTUNITY BODY (hero + tabs + panels)
// ============================================================================

function OpportunityBody({
  shortlistPublicKey,
  shortlistName,
  shortlistStatus,
  isPending,
  isSuccess,
  items,
  totalCount,
  pageNumber,
  isFetching,
  onPageChange,
}: {
  shortlistPublicKey: string;
  shortlistName: string | null;
  shortlistStatus: number | null;
  isPending: boolean;
  isSuccess: boolean;
  items: TalentListItem[];
  totalCount: number;
  pageNumber: number;
  isFetching: boolean;
  onPageChange: (n: number) => void;
}) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabKey>('candidates');

  return (
    <>
      <Hero
        shortlistName={shortlistName}
        shortlistStatus={shortlistStatus}
        totalCount={totalCount}
        items={items}
      />

      <TabsNav activeTab={activeTab} onChange={setActiveTab} candidatesCount={totalCount} />

      {activeTab === 'candidates' && (
        <Box
          role="tabpanel"
          id="tabpanel-candidates"
          aria-labelledby="tab-candidates"
          data-testid="opportunity-detail-candidates-panel"
        >
          {isPending && <LoadingSkeleton />}
          {isSuccess && items.length === 0 && <EmptyState />}
          {isSuccess && items.length > 0 && (
            <CandidatesPanel
              shortlistPublicKey={shortlistPublicKey}
              items={items}
              totalCount={totalCount}
              pageNumber={pageNumber}
              isFetching={isFetching}
              onPageChange={onPageChange}
            />
          )}
        </Box>
      )}

      {activeTab === 'overview' && (
        <ComingSoonPanel
          tabKey="overview"
          eyebrow={t('opportunities.detail.overview.eyebrow')}
          title={t('opportunities.detail.overview.title')}
          message={t('opportunities.detail.overview.message')}
        />
      )}

      {activeTab === 'comments' && (
        <ComingSoonPanel
          tabKey="comments"
          eyebrow={t('opportunities.detail.comments.eyebrow')}
          title={t('opportunities.detail.comments.title')}
          message={t('opportunities.detail.comments.message')}
        />
      )}

      {activeTab === 'activity' && (
        <ComingSoonPanel
          tabKey="activity"
          eyebrow={t('opportunities.detail.activity.eyebrow')}
          title={t('opportunities.detail.activity.title')}
          message={t('opportunities.detail.activity.message')}
        />
      )}
    </>
  );
}

// ============================================================================
// HERO
// ============================================================================

function Hero({
  shortlistName,
  shortlistStatus,
  totalCount,
  items,
}: {
  shortlistName: string | null;
  shortlistStatus: number | null;
  totalCount: number;
  items: TalentListItem[];
}) {
  const { t } = useTranslation();

  // Stats card 4-up. "Presented" = totalCount; los otros se derivan a partir
  // de TalentActions agregadas en el componente padre — pero como aggregateMode
  // requiere lookup por-tarjeta, en el hero solo mostramos Presented + el resto en 0
  // hasta que tengamos un endpoint de stats. (Defer SW-145 stats endpoint.)
  const presentedCount = totalCount;
  // En MVP no podemos saber new/seen/dismissed sin pegarle al endpoint por talent.
  // Mostramos los slots pero con "—" para los desconocidos — honesto y no inventa.
  const unknown = '—';

  // Si el dataset es pequeño podemos pre-cargar siempre que items.length === totalCount
  // y simplemente contar las actions ya cacheadas. Para no hacer prefetch eagerly,
  // en MVP dejamos los slots como unknown.

  const isClosed = shortlistStatus === 2;
  const title = shortlistName ?? t('opportunities.detail.titleFallback');
  // El número total de candidatos también se loguea como subtítulo via el chip count
  // de la tab activa; aquí solo trabajamos meta del rol.

  return (
    <Box
      component="section"
      data-testid="opportunity-detail-hero"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
        gap: '32px',
        pb: '26px',
        mb: '28px',
        borderBottom: `1px solid ${tokens.color.line}`,
      }}
    >
      <Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            mb: '14px',
            fontFamily: tokens.font.mono,
            fontSize: 11,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: tokens.color.muted,
          }}
        >
          <Box
            component="span"
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: isClosed ? tokens.color.muted2 : tokens.color.pos,
              boxShadow: isClosed ? 'none' : `0 0 0 4px ${tokens.color.posSoft}`,
            }}
          />
          {isClosed
            ? t('opportunities.detail.eyebrow.closed')
            : t('opportunities.detail.eyebrow.active')}
        </Box>
        <Typography
          variant="h1"
          data-testid="opportunity-detail-title"
          sx={{
            fontFamily: tokens.font.display,
            fontWeight: 700,
            letterSpacing: '-0.025em',
            fontSize: { xs: 28, md: 38 },
            lineHeight: 1.05,
            m: 0,
          }}
        >
          {title}
        </Typography>
        {totalCount > 0 && (
          <Typography
            sx={{
              mt: '10px',
              color: tokens.color.muted,
              fontSize: 13,
              fontFamily: tokens.font.mono,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}
            data-testid="opportunity-detail-talent-count"
          >
            {t('opportunities.detail.talentCount', { count: totalCount })}
          </Typography>
        )}
      </Box>

      <Box
        data-testid="opportunity-detail-stats"
        sx={{
          display: 'flex',
          gap: '8px',
          alignItems: 'stretch',
          flexWrap: 'wrap',
        }}
      >
        <StatCell label={t('opportunities.detail.stats.presented')} value={String(presentedCount)} accent />
        <StatCell label={t('opportunities.detail.stats.new')} value={unknown} />
        <StatCell label={t('opportunities.detail.stats.seen')} value={unknown} />
        <StatCell label={t('opportunities.detail.stats.dismissed')} value={unknown} />
      </Box>

      {/* Items se referencian aquí solo para mantener el contract con stats
          en versión futura — actualmente no derivamos nada del array. */}
      <Box sx={{ display: 'none' }} aria-hidden data-talents-loaded={items.length} />
    </Box>
  );
}

function StatCell({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <Box
      sx={{
        background: accent ? tokens.color.accent : tokens.color.surface,
        border: `1px solid ${accent ? tokens.color.accent : tokens.color.line}`,
        borderRadius: '14px',
        padding: '14px 18px',
        minWidth: 110,
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        color: accent ? tokens.color.accentInk : tokens.color.ink,
      }}
    >
      <Box
        component="span"
        sx={{
          fontFamily: tokens.font.mono,
          fontSize: 10.5,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: accent ? tokens.color.accentInk : tokens.color.muted,
          opacity: accent ? 0.85 : 1,
        }}
      >
        {label}
      </Box>
      <Box
        component="span"
        sx={{
          fontFamily: tokens.font.display,
          fontSize: 24,
          fontWeight: 700,
          letterSpacing: '-0.02em',
        }}
      >
        {value}
      </Box>
    </Box>
  );
}

// ============================================================================
// TABS NAV
// ============================================================================

function TabsNav({
  activeTab,
  onChange,
  candidatesCount,
}: {
  activeTab: TabKey;
  onChange: (k: TabKey) => void;
  candidatesCount: number;
}) {
  const { t } = useTranslation();
  const items: Array<{ key: TabKey; label: string; count?: number }> = [
    { key: 'candidates', label: t('opportunities.detail.tabs.candidates'), count: candidatesCount },
    { key: 'overview', label: t('opportunities.detail.tabs.overview') },
    { key: 'comments', label: t('opportunities.detail.tabs.comments') },
    { key: 'activity', label: t('opportunities.detail.tabs.activity') },
  ];

  const handleKey = (e: KeyboardEvent<HTMLButtonElement>, idx: number) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const next = items[(idx + 1) % items.length]!;
      onChange(next.key);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prev = items[(idx - 1 + items.length) % items.length]!;
      onChange(prev.key);
    } else if (e.key === 'Home') {
      e.preventDefault();
      onChange(items[0]!.key);
    } else if (e.key === 'End') {
      e.preventDefault();
      onChange(items[items.length - 1]!.key);
    }
  };

  return (
    <Box
      role="tablist"
      aria-label={t('opportunities.detail.tabs.ariaLabel')}
      data-testid="opportunity-detail-tabs"
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: '2px',
        borderBottom: `1px solid ${tokens.color.line}`,
        mb: '26px',
        overflowX: 'auto',
      }}
    >
      {items.map((it, idx) => {
        const isActive = activeTab === it.key;
        return (
          <Box
            key={it.key}
            component="button"
            type="button"
            role="tab"
            id={`tab-${it.key}`}
            aria-selected={isActive}
            aria-controls={`tabpanel-${it.key}`}
            tabIndex={isActive ? 0 : -1}
            data-testid={`tab-${it.key}`}
            onClick={() => onChange(it.key)}
            onKeyDown={(e) => handleKey(e, idx)}
            sx={{
              appearance: 'none',
              position: 'relative',
              background: 'transparent',
              border: 0,
              padding: '10px 16px 14px',
              fontFamily: 'inherit',
              fontSize: 14,
              fontWeight: isActive ? 600 : 500,
              color: isActive ? tokens.color.ink : tokens.color.muted,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              whiteSpace: 'nowrap',
              transition: 'color .12s',
              '&:hover': { color: tokens.color.ink },
              '&::after': isActive
                ? {
                    content: '""',
                    position: 'absolute',
                    left: '12px',
                    right: '12px',
                    bottom: '-1px',
                    height: '2px',
                    background: tokens.color.ink,
                    borderRadius: '2px 2px 0 0',
                  }
                : {},
            }}
          >
            {it.label}
            {it.count !== undefined && it.count > 0 && (
              <Box
                component="span"
                sx={{
                  fontFamily: tokens.font.mono,
                  fontSize: 10.5,
                  fontWeight: 500,
                  background: isActive ? tokens.color.ink : tokens.color.surface2,
                  border: `1px solid ${isActive ? tokens.color.ink : tokens.color.line}`,
                  color: isActive ? tokens.color.accentInk : tokens.color.muted,
                  padding: '1px 7px',
                  borderRadius: tokens.size.radiusPill,
                }}
              >
                {it.count}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

// ============================================================================
// CANDIDATES PANEL (toolbar + category group + cards)
// ============================================================================

function CandidatesPanel({
  shortlistPublicKey,
  items,
  totalCount,
  pageNumber,
  isFetching,
  onPageChange,
}: {
  shortlistPublicKey: string;
  items: TalentListItem[];
  totalCount: number;
  pageNumber: number;
  isFetching: boolean;
  onPageChange: (n: number) => void;
}) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');
  const [groupCollapsed, setGroupCollapsed] = useState(false);
  // Status por tarjeta: lo populamos via callback desde cada `TalentCard` (cada una
  // ya hace su query). En MVP el filtro por status filtra solo lo conocido —
  // unknown (sin actions cargadas aún) se asume 'new'.
  const [statusMap, setStatusMap] = useState<Record<string, CandidateStatus>>({});

  // useCallback para mantener identidad estable — los hijos lo usan en deps de effect.
  const handleStatusChange = useCallback((id: string, status: CandidateStatus) => {
    setStatusMap((prev) => (prev[id] === status ? prev : { ...prev, [id]: status }));
  }, []);

  // Filtrado memoizado: search es full-text sobre name + company + skills + seniority.
  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((talent) => {
      const status = statusMap[talent.id] ?? 'new';
      // Filtro por status. "All" no filtra; "seen" en MVP es alias de "new" (no hay
      // tracking de viewed) — al menos los talent sin action explícita aparecen ahí.
      if (filter === 'new' && status !== 'new') return false;
      if (filter === 'dismissed' && status !== 'dismissed') return false;
      // "seen" en MVP: mostramos solo los que no son dismissed ni interested aún;
      // efectivamente igual que 'new' por ahora.
      if (filter === 'seen' && status !== 'new' && status !== 'seen') return false;

      if (!q) return true;
      const haystack = [
        talent.fullName,
        talent.currentCompany ?? '',
        talent.seniority ?? '',
        talent.city ?? '',
        talent.country ?? '',
        talent.topSkills.join(' '),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [items, statusMap, filter, search]);

  // Counts por filter chip (memoized).
  const counts = useMemo(() => {
    let n = 0;
    let s = 0;
    let d = 0;
    for (const it of items) {
      const status = statusMap[it.id] ?? 'new';
      if (status === 'new') n += 1;
      if (status === 'seen' || status === 'new') s += 1;
      if (status === 'dismissed') d += 1;
    }
    return { all: items.length, new: n, seen: s, dismissed: d };
  }, [items, statusMap]);

  const showGroup = filteredItems.length > 0;
  const showFilteredEmpty = items.length > 0 && filteredItems.length === 0;

  return (
    <>
      <Toolbar
        filter={filter}
        onFilterChange={setFilter}
        search={search}
        onSearchChange={setSearch}
        counts={counts}
        groupCollapsed={groupCollapsed}
        onToggleAll={() => setGroupCollapsed((c) => !c)}
      />

      {showGroup && (
        <CategoryGroup
          title={t('opportunities.detail.group.allTitle')}
          subtitle={t('opportunities.detail.group.allSub')}
          count={filteredItems.length}
          collapsed={groupCollapsed}
          onToggle={() => setGroupCollapsed((c) => !c)}
        >
          <Box
            data-testid="talents-list"
            sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
          >
            {filteredItems.map((talent) => (
              <TalentCard
                key={talent.id}
                talent={talent}
                shortlistPublicKey={shortlistPublicKey}
                onStatusChange={handleStatusChange}
              />
            ))}
          </Box>
        </CategoryGroup>
      )}

      {showFilteredEmpty && <FilteredEmptyState onClear={() => { setFilter('all'); setSearch(''); }} />}

      {totalCount > PAGE_SIZE && (
        <Pagination
          page={pageNumber}
          pageSize={PAGE_SIZE}
          total={totalCount}
          isFetching={isFetching}
          onChange={onPageChange}
        />
      )}
    </>
  );
}

// ============================================================================
// TOOLBAR
// ============================================================================

function Toolbar({
  filter,
  onFilterChange,
  search,
  onSearchChange,
  counts,
  groupCollapsed,
  onToggleAll,
}: {
  filter: FilterKey;
  onFilterChange: (k: FilterKey) => void;
  search: string;
  onSearchChange: (s: string) => void;
  counts: { all: number; new: number; seen: number; dismissed: number };
  groupCollapsed: boolean;
  onToggleAll: () => void;
}) {
  const { t } = useTranslation();
  const filterItems: Array<{ key: FilterKey; label: string; count: number }> = [
    { key: 'all', label: t('opportunities.detail.toolbar.all'), count: counts.all },
    { key: 'new', label: t('opportunities.detail.toolbar.new'), count: counts.new },
    { key: 'seen', label: t('opportunities.detail.toolbar.seen'), count: counts.seen },
    { key: 'dismissed', label: t('opportunities.detail.toolbar.dismissed'), count: counts.dismissed },
  ];

  return (
    <Box
      data-testid="opportunity-detail-toolbar"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: '18px',
        gap: '16px',
        flexWrap: 'wrap',
      }}
    >
      <Box
        data-testid="opportunity-detail-filter-chips"
        role="group"
        aria-label={t('opportunities.detail.toolbar.filterGroupAria')}
        sx={{
          display: 'flex',
          gap: '4px',
          background: tokens.color.surface,
          padding: '4px',
          borderRadius: '10px',
          border: `1px solid ${tokens.color.line}`,
        }}
      >
        {filterItems.map((it) => {
          const isActive = filter === it.key;
          return (
            <Box
              key={it.key}
              component="button"
              type="button"
              data-testid={`filter-chip-${it.key}`}
              aria-pressed={isActive}
              onClick={() => onFilterChange(it.key)}
              sx={{
                padding: '6px 12px',
                borderRadius: '7px',
                fontSize: 13,
                cursor: 'pointer',
                border: 0,
                background: isActive ? tokens.color.surface2 : 'transparent',
                color: isActive ? tokens.color.ink : tokens.color.muted,
                fontWeight: isActive ? 600 : 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                fontFamily: 'inherit',
              }}
            >
              {it.label}
              <Box
                component="span"
                sx={{
                  fontFamily: tokens.font.mono,
                  fontSize: 10.5,
                  padding: '1px 6px',
                  background: isActive ? tokens.color.ink : tokens.color.surface,
                  border: `1px solid ${isActive ? tokens.color.ink : tokens.color.line}`,
                  borderRadius: tokens.size.radiusPill,
                  color: isActive ? tokens.color.accentInk : tokens.color.muted,
                }}
              >
                {it.count}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <Box sx={{ position: 'relative', height: 34, width: { xs: '100%', sm: 240 } }}>
          <Box
            component="svg"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            aria-hidden
            sx={{
              position: 'absolute',
              left: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 14,
              height: 14,
              color: tokens.color.muted,
            }}
          >
            <circle cx="7" cy="7" r="4.5" />
            <path d="M10.5 10.5l3 3" />
          </Box>
          <Box
            component="input"
            type="search"
            data-testid="opportunity-detail-search"
            placeholder={t('opportunities.detail.toolbar.search.placeholder')}
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
            sx={{
              width: '100%',
              height: '100%',
              padding: '0 12px 0 32px',
              borderRadius: '10px',
              border: `1px solid ${tokens.color.line}`,
              background: tokens.color.surface,
              fontSize: 13,
              fontFamily: 'inherit',
              color: tokens.color.ink,
              '&:focus': { outline: 0, borderColor: tokens.color.ink },
            }}
          />
        </Box>
        <Box
          component="button"
          type="button"
          data-testid="opportunity-detail-toggle-all"
          onClick={onToggleAll}
          sx={{
            height: 34,
            padding: '0 12px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: tokens.color.surface,
            border: `1px solid ${tokens.color.line}`,
            borderRadius: '10px',
            color: tokens.color.ink,
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
            fontFamily: 'inherit',
            '&:hover': { borderColor: tokens.color.ink },
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            aria-hidden
            sx={{ width: 14, height: 14 }}
          >
            <path d="M3 6h10M3 10h10" />
          </Box>
          {groupCollapsed
            ? t('opportunities.detail.toolbar.expandAll')
            : t('opportunities.detail.toolbar.collapseAll')}
        </Box>
      </Box>
    </Box>
  );
}

// ============================================================================
// CATEGORY GROUP
// ============================================================================

function CategoryGroup({
  title,
  subtitle,
  count,
  collapsed,
  onToggle,
  children,
}: {
  title: string;
  subtitle: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Box data-testid="opportunity-detail-category-group" sx={{ mb: '28px' }}>
      <Box
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          background: tokens.color.surface,
          border: `1px solid ${tokens.color.line}`,
          borderRadius: '14px',
          mb: '12px',
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'border-color .15s',
          '&:hover': { borderColor: tokens.color.ink },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              display: 'grid',
              placeItems: 'center',
              background: tokens.color.accentSoft,
              color: tokens.color.accent,
            }}
          >
            <Box
              component="svg"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              aria-hidden
              sx={{ width: 18, height: 18 }}
            >
              <path d="M2 5l6 4 6-4" />
              <path d="M2 5v6l6 4 6-4V5" />
            </Box>
          </Box>
          <Box>
            <Typography
              component="h2"
              sx={{
                fontFamily: tokens.font.display,
                fontSize: 17,
                fontWeight: 600,
                letterSpacing: '-0.015em',
                m: 0,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              {title}
              <Box
                component="span"
                sx={{
                  fontFamily: tokens.font.mono,
                  fontSize: 12,
                  padding: '3px 10px',
                  background: tokens.color.ink,
                  color: tokens.color.accentInk,
                  borderRadius: tokens.size.radiusPill,
                  fontWeight: 500,
                }}
              >
                {count}
              </Box>
            </Typography>
            <Box
              sx={{
                color: tokens.color.muted,
                fontSize: 12.5,
                mt: '2px',
                fontFamily: tokens.font.mono,
                letterSpacing: '0.02em',
              }}
            >
              {subtitle}
            </Box>
          </Box>
        </Box>
        <Box
          component="span"
          aria-label={t('opportunities.detail.group.toggleAria')}
          sx={{
            background: tokens.color.surface2,
            border: `1px solid ${tokens.color.line}`,
            width: 32,
            height: 32,
            borderRadius: '8px',
            display: 'grid',
            placeItems: 'center',
            color: tokens.color.muted,
            transition: 'transform .25s ease',
            transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            aria-hidden
            sx={{ width: 14, height: 14 }}
          >
            <path d="M4 6l4 4 4-4" />
          </Box>
        </Box>
      </Box>

      {!collapsed && children}
    </Box>
  );
}

// ============================================================================
// TALENT CARD
// ============================================================================

function TalentCard({
  talent,
  shortlistPublicKey,
  onStatusChange,
}: {
  talent: TalentListItem;
  shortlistPublicKey: string;
  onStatusChange: (id: string, status: CandidateStatus) => void;
}) {
  const { t } = useTranslation();
  const notify = useNotify();

  const setAction = useSetTalentAction({
    shortlistPublicKey,
    talentPublicKey: talent.id,
  });

  // SW-145: el BE incluye `myAction` (enum del backend en PascalCase) en cada
  // item del list response. Antes el FE hacía GET /actions per card = N+1
  // (20 talents = 21 round trips). Ahora derivamos el status directo del item
  // — la lista trae el state inicial y la mutation invalida el list query.
  const derivedStatus = useMemo<CandidateStatus>(() => {
    if (talent.myAction === 'NotInterested') return 'dismissed';
    if (talent.myAction === 'Interested') return 'interested';
    return 'new';
  }, [talent.myAction]);

  // Propagamos el status al padre vía effect (idempotente — el padre dedupea
  // por igualdad de valor en su setter funcional). Se ejecuta una vez al mount
  // y cuando la mutation invalida el list query (refetch → new talent.myAction).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    if (mountedRef.current) {
      onStatusChange(talent.id, derivedStatus);
    }
  }, [derivedStatus, talent.id, onStatusChange]);

  // SW-144b code-review m-3: useCallback para estabilizar identidad del handler
  // entre renders. Consistente con `handleStatusChange` del padre.
  const handleDismissOrRestore = useCallback(() => {
    const nextType: TalentActionType = derivedStatus === 'dismissed' ? 'interested' : 'not_interested';
    setAction.mutate(
      { type: nextType },
      {
        onSuccess: () => {
          if (!mountedRef.current) return;
          if (nextType === 'not_interested') {
            notify.warning(t('opportunities.detail.candidate.dismissed', { name: talent.fullName }));
          } else {
            notify.success(t('opportunities.detail.candidate.restored', { name: talent.fullName }));
          }
        },
        onError: () => {
          if (!mountedRef.current) return;
          notify.error(t('opportunities.detail.candidate.actionError'));
        },
      },
    );
  }, [derivedStatus, setAction, notify, t, talent.fullName]);

  const handleShare = useCallback(async () => {
    const url = `${window.location.origin}/opportunities/${shortlistPublicKey}/talents/${talent.id}`;
    try {
      await navigator.clipboard.writeText(url);
      if (mountedRef.current) {
        notify.success(t('opportunities.detail.candidate.shareCopied', { name: talent.fullName }));
      }
    } catch {
      if (mountedRef.current) {
        notify.error(t('opportunities.detail.actions.shareError'));
      }
    }
  }, [shortlistPublicKey, talent.id, talent.fullName, notify, t]);

  const initials = computeInitials(talent.fullName);
  const avatarGradient = AVATAR_GRADIENTS[hashIndex(talent.id, AVATAR_GRADIENTS.length)]!;
  const location = formatLocation(talent.city, talent.country);
  const role = talent.seniority?.trim() || t('opportunities.detail.candidate.roleFallback');
  const isDismissed = derivedStatus === 'dismissed';
  const dataState: 'active' | 'dismissed' = isDismissed ? 'dismissed' : 'active';

  return (
    <Box
      component="article"
      data-testid="talent-card"
      data-talent-id={talent.id}
      data-status={derivedStatus}
      data-state={dataState}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '56px 1fr', md: '56px 1fr 220px' },
        gap: { xs: '16px', md: '22px' },
        alignItems: 'stretch',
        background: tokens.color.surface,
        border: `1px solid ${tokens.color.line}`,
        borderRadius: '14px',
        padding: '20px',
        boxShadow: isDismissed
          ? 'none'
          : '0 1px 0 rgba(15,15,15,.04), 0 8px 24px -12px rgba(15,15,15,.10)',
        opacity: isDismissed ? 0.55 : 1,
        transition: 'border-color .15s, opacity .15s, box-shadow .15s',
        '&:hover': { borderColor: isDismissed ? tokens.color.line : tokens.color.ink, opacity: isDismissed ? 0.75 : 1 },
      }}
    >
      <Box
        data-testid="talent-avatar"
        sx={{
          width: 56,
          height: 56,
          borderRadius: '14px',
          display: 'grid',
          placeItems: 'center',
          fontFamily: tokens.font.display,
          fontWeight: 600,
          fontSize: 18,
          color: tokens.color.accentInk,
          letterSpacing: '0.02em',
          background: avatarGradient,
        }}
      >
        {initials}
      </Box>

      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <Typography
            data-testid="talent-name"
            component="span"
            sx={{
              fontFamily: tokens.font.display,
              fontSize: 17,
              fontWeight: 600,
              letterSpacing: '-0.015em',
              color: tokens.color.ink,
            }}
          >
            {talent.fullName}
          </Typography>
          <Box
            component="span"
            sx={{ width: 3, height: 3, borderRadius: '50%', background: tokens.color.muted2 }}
          />
          <Box
            component="span"
            data-testid="talent-role"
            sx={{ color: tokens.color.accent, fontSize: 14.5, fontWeight: 600 }}
          >
            {role}
          </Box>
          {(location || talent.currentCompany) && (
            <Box
              component="span"
              data-testid="talent-location"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                color: tokens.color.muted,
                fontSize: 12.5,
                ml: '4px',
              }}
            >
              <Box
                component="svg"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                aria-hidden
                sx={{ width: 12, height: 12 }}
              >
                <path d="M8 14s5-4.5 5-8.5A5 5 0 003 5.5C3 9.5 8 14 8 14z" />
                <circle cx="8" cy="6" r="1.7" />
              </Box>
              {location ?? t('opportunities.detail.candidate.location.unknown')}
              {talent.currentCompany && (
                <>
                  <Box
                    component="span"
                    sx={{ width: 3, height: 3, borderRadius: '50%', background: tokens.color.muted2, mx: '2px' }}
                  />
                  <Box component="span" data-testid="talent-company">
                    {talent.currentCompany}
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>

        {talent.topSkills.length > 0 && (
          <Box data-testid="talent-skills" sx={{ mt: '4px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '84px minmax(0,1fr)' },
                gap: '14px',
                alignItems: 'start',
                minHeight: 26,
              }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: tokens.font.mono,
                  fontSize: 10,
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.12em',
                  color: tokens.color.muted2,
                  paddingTop: '7px',
                  whiteSpace: 'nowrap',
                  borderTop: { xs: 'none', md: `1px dashed ${tokens.color.line}` },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    width: 4,
                    height: 4,
                    borderRadius: '50%',
                    background: tokens.color.accent,
                    verticalAlign: '2px',
                    mr: '7px',
                  }}
                />
                {t('opportunities.detail.candidate.coreSkills')}
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                {talent.topSkills.map((skill) => (
                  <SkillChip key={skill}>{skill}</SkillChip>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: '10px',
          borderLeft: { xs: 'none', md: `1px dashed ${tokens.color.line}` },
          borderTop: { xs: `1px dashed ${tokens.color.line}`, md: 'none' },
          paddingLeft: { xs: 0, md: '22px' },
          paddingTop: { xs: '14px', md: 0 },
          gridColumn: { xs: '1 / -1', md: 'auto' },
        }}
      >
        <Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1px',
              background: tokens.color.lineSoft,
              border: `1px solid ${tokens.color.lineSoft}`,
              borderRadius: '10px',
              overflow: 'hidden',
            }}
          >
            <SummaryCell
              label={t('opportunities.detail.candidate.level')}
              value={talent.seniority ?? '—'}
            />
            <SummaryCell
              label={t('opportunities.detail.candidate.rate')}
              value={t('opportunities.detail.candidate.rateUnavailable')}
              dataTestId="talent-rate"
            />
          </Box>
          <Box sx={{ mt: '10px' }}>
            <StatusPill status={derivedStatus} />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: '6px',
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
            mt: '8px',
          }}
        >
          <CardActionButton
            variant={isDismissed ? 'restore' : 'dismiss'}
            data-testid={isDismissed ? 'talent-action-restore' : 'talent-action-dismiss'}
            onClick={handleDismissOrRestore}
            disabled={setAction.isPending}
            label={
              isDismissed
                ? t('opportunities.detail.candidate.restore')
                : t('opportunities.detail.candidate.dismiss')
            }
            icon={
              isDismissed ? (
                <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
                  <path d="M3 8a5 5 0 015-5c2 0 3.7 1.2 4.5 3M13 3v3h-3" />
                </svg>
              ) : (
                <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
                  <path d="M3 5h10M6 5V3.5h4V5M5 5l.7 8h4.6L11 5" />
                </svg>
              )
            }
          />
          <CardActionButton
            variant="ghost"
            data-testid="talent-action-share"
            onClick={handleShare}
            label={t('opportunities.detail.candidate.share')}
            icon={
              <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
                <path d="M6 9.5l4-3M6 6.5l4 3M3.5 8a2 2 0 100-4 2 2 0 000 4zm9-3a2 2 0 100-4 2 2 0 000 4zm0 8a2 2 0 100-4 2 2 0 000 4z" />
              </svg>
            }
          />
          <Box
            component={Link}
            to={`/opportunities/${shortlistPublicKey}/talents/${talent.id}`}
            data-testid="talent-action-view"
            sx={{
              height: 32,
              padding: '0 12px',
              borderRadius: '8px',
              fontSize: 12.5,
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              border: `1px solid ${tokens.color.accent}`,
              background: tokens.color.accent,
              color: tokens.color.accentInk,
              textDecoration: 'none',
              transition: 'opacity .15s',
              '&:hover': { opacity: 0.92 },
            }}
          >
            <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
              <path d="M2 8s2.5-4.5 6-4.5S14 8 14 8s-2.5 4.5-6 4.5S2 8 2 8z" />
              <circle cx="8" cy="8" r="2" />
            </svg>
            {t('opportunities.detail.candidate.view')}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function SummaryCell({
  label,
  value,
  dataTestId,
}: {
  label: string;
  value: string;
  dataTestId?: string;
}) {
  return (
    <Box
      data-testid={dataTestId}
      sx={{
        background: tokens.color.surface,
        padding: '8px 10px',
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
      }}
    >
      <Box
        component="span"
        sx={{
          fontFamily: tokens.font.mono,
          fontSize: 9.5,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: tokens.color.muted,
        }}
      >
        {label}
      </Box>
      <Box
        component="span"
        sx={{
          fontFamily: tokens.font.display,
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: '-0.005em',
          color: tokens.color.ink,
        }}
      >
        {value}
      </Box>
    </Box>
  );
}

function StatusPill({ status }: { status: CandidateStatus }) {
  const { t } = useTranslation();
  const config = STATUS_PILL_CONFIG[status];
  return (
    <Box
      component="span"
      data-testid="talent-status-pill"
      data-status={status}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 10px',
        borderRadius: tokens.size.radiusPill,
        fontSize: 11.5,
        fontWeight: 600,
        fontFamily: tokens.font.mono,
        letterSpacing: '0.02em',
        background: config.bg,
        color: config.color,
        border: config.border ? `1px solid ${config.border}` : 'none',
      }}
    >
      <Box
        component="span"
        sx={{ width: 5, height: 5, borderRadius: '50%', background: config.dot }}
      />
      {t(config.labelKey)}
    </Box>
  );
}

const STATUS_PILL_CONFIG: Record<
  CandidateStatus,
  { bg: string; color: string; dot: string; border?: string; labelKey: string }
> = {
  new: {
    bg: tokens.color.accentSoft,
    color: tokens.color.accent,
    dot: tokens.color.accent,
    labelKey: 'opportunities.detail.candidate.statusNew',
  },
  seen: {
    bg: tokens.color.surface2,
    color: tokens.color.muted,
    dot: tokens.color.muted2,
    border: tokens.color.line,
    labelKey: 'opportunities.detail.candidate.statusSeen',
  },
  dismissed: {
    bg: tokens.color.warnSoft,
    color: tokens.color.warn,
    dot: tokens.color.warn,
    labelKey: 'opportunities.detail.candidate.statusDismissed',
  },
  interested: {
    bg: tokens.color.posSoft,
    color: tokens.color.pos,
    dot: tokens.color.pos,
    labelKey: 'opportunities.detail.candidate.statusInterested',
  },
};

function CardActionButton({
  variant,
  label,
  icon,
  onClick,
  disabled,
  ...rest
}: {
  variant: 'dismiss' | 'restore' | 'ghost';
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  'data-testid'?: string;
}) {
  const variantStyles: Record<string, CSSProperties> = {
    dismiss: {
      color: tokens.color.muted,
      borderColor: tokens.color.line,
      background: tokens.color.surface,
    },
    restore: {
      color: tokens.color.accent,
      borderColor: tokens.color.accent,
      background: tokens.color.accentSoft,
    },
    ghost: {
      color: tokens.color.ink,
      borderColor: tokens.color.line,
      background: tokens.color.surface,
    },
  };
  const s = variantStyles[variant]!;
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={rest['data-testid']}
      sx={{
        height: 32,
        padding: '0 12px',
        borderRadius: '8px',
        fontSize: 12.5,
        fontWeight: 600,
        cursor: disabled ? 'wait' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        border: `1px solid ${s.borderColor}`,
        background: s.background,
        color: s.color,
        fontFamily: 'inherit',
        opacity: disabled ? 0.6 : 1,
        transition: 'border-color .15s, opacity .15s',
        '&:hover': { borderColor: tokens.color.ink },
      }}
    >
      {icon}
      {label}
    </Box>
  );
}

function SkillChip({ children }: { children: string }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 10px',
        borderRadius: '7px',
        background: tokens.color.accent,
        color: tokens.color.accentInk,
        fontFamily: tokens.font.display,
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: '-0.005em',
        border: `1px solid ${tokens.color.accent}`,
        whiteSpace: 'nowrap',
        lineHeight: 1.3,
      }}
    >
      {children}
    </Box>
  );
}

// ============================================================================
// EMPTY / NOT FOUND / LOADING / COMING SOON
// ============================================================================

function LoadingSkeleton() {
  return (
    <Box
      data-testid="opportunity-detail-loading"
      sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
    >
      {Array.from({ length: 4 }).map((_, i) => (
        <Box
          key={i}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '56px 1fr', md: '56px 1fr 220px' },
            gap: '22px',
            alignItems: 'center',
            background: tokens.color.surface,
            border: `1px solid ${tokens.color.line}`,
            borderRadius: '14px',
            padding: '20px',
          }}
        >
          <Skeleton variant="rectangular" width={56} height={56} sx={{ borderRadius: '14px' }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Skeleton variant="text" width="40%" sx={{ fontSize: 17 }} />
            <Skeleton variant="text" width="60%" sx={{ fontSize: 12.5 }} />
            <Box sx={{ display: 'flex', gap: '6px', mt: '6px' }}>
              <Skeleton variant="rectangular" width={70} height={22} sx={{ borderRadius: '7px' }} />
              <Skeleton variant="rectangular" width={70} height={22} sx={{ borderRadius: '7px' }} />
              <Skeleton variant="rectangular" width={70} height={22} sx={{ borderRadius: '7px' }} />
            </Box>
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', gap: '8px' }}>
            <Skeleton variant="rectangular" height={40} sx={{ borderRadius: '10px' }} />
            <Skeleton variant="rectangular" height={20} width="55%" sx={{ borderRadius: '999px' }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <Box
      data-testid="opportunity-detail-empty"
      sx={{
        background: tokens.color.surface,
        border: `1px dashed ${tokens.color.line}`,
        borderRadius: '14px',
        padding: '48px 28px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
        {t('opportunities.detail.empty.title')}
      </Typography>
      <Typography sx={{ color: tokens.color.muted, fontSize: 14, maxWidth: 480, mx: 'auto' }}>
        {t('opportunities.detail.empty.message')}
      </Typography>
    </Box>
  );
}

function FilteredEmptyState({ onClear }: { onClear: () => void }) {
  const { t } = useTranslation();
  return (
    <Box
      data-testid="opportunity-detail-filtered-empty"
      sx={{
        background: tokens.color.surface,
        border: `1px dashed ${tokens.color.line}`,
        borderRadius: '14px',
        padding: '32px 24px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        alignItems: 'center',
      }}
    >
      <Typography sx={{ fontSize: 14, color: tokens.color.muted }}>
        {t('opportunities.detail.filtered.empty')}
      </Typography>
      <Box
        component="button"
        type="button"
        onClick={onClear}
        data-testid="opportunity-detail-filtered-clear"
        sx={{
          height: 32,
          padding: '0 14px',
          background: tokens.color.surface2,
          border: `1px solid ${tokens.color.line}`,
          borderRadius: '8px',
          color: tokens.color.ink,
          fontSize: 12.5,
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: 'inherit',
          '&:hover': { borderColor: tokens.color.ink },
        }}
      >
        {t('opportunities.detail.filtered.clear')}
      </Box>
    </Box>
  );
}

function NotFoundState() {
  const { t } = useTranslation();
  return (
    <Box
      data-testid="opportunity-detail-not-found"
      sx={{ display: 'flex', flexDirection: 'column', gap: '12px', mt: '24px' }}
    >
      <Alert severity="info" variant="outlined">
        <Typography sx={{ fontWeight: 600, mb: '4px' }}>
          {t('opportunities.detail.notFound.title')}
        </Typography>
        <Typography sx={{ fontSize: 13 }}>{t('opportunities.detail.notFound.message')}</Typography>
      </Alert>
    </Box>
  );
}

function ComingSoonPanel({
  tabKey,
  eyebrow,
  title,
  message,
}: {
  tabKey: TabKey;
  eyebrow: string;
  title: string;
  message: string;
}) {
  return (
    <Box
      role="tabpanel"
      id={`tabpanel-${tabKey}`}
      aria-labelledby={`tab-${tabKey}`}
      data-testid={`opportunity-detail-${tabKey}-panel`}
      sx={{
        background: tokens.color.surface,
        border: `1px dashed ${tokens.color.line}`,
        borderRadius: '14px',
        padding: '40px 28px',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        alignItems: 'center',
      }}
    >
      <Box
        component="span"
        sx={{
          fontFamily: tokens.font.mono,
          fontSize: 10.5,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: tokens.color.muted2,
        }}
      >
        {eyebrow}
      </Box>
      <Typography sx={{ fontSize: 18, fontWeight: 600 }}>{title}</Typography>
      <Typography sx={{ color: tokens.color.muted, fontSize: 14, maxWidth: 480, mx: 'auto' }}>
        {message}
      </Typography>
    </Box>
  );
}

// ============================================================================
// PAGINATION
// ============================================================================

function Pagination({
  page,
  pageSize,
  total,
  isFetching,
  onChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  isFetching: boolean;
  onChange: (page: number) => void;
}) {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (totalPages === 1) return null;

  return (
    <Box
      data-testid="opportunity-detail-pagination"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        py: '10px',
        mt: '12px',
      }}
    >
      <Typography
        sx={{
          fontFamily: tokens.font.mono,
          fontSize: 11,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: tokens.color.muted,
        }}
      >
        {t('opportunities.pagination.page', { page, total: totalPages })}
      </Typography>
      <Box sx={{ display: 'flex', gap: '8px' }}>
        <PaginationButton
          data-testid="talents-prev"
          disabled={page <= 1 || isFetching}
          onClick={() => onChange(page - 1)}
        >
          {t('opportunities.pagination.previous')}
        </PaginationButton>
        <PaginationButton
          data-testid="talents-next"
          disabled={page >= totalPages || isFetching}
          onClick={() => onChange(page + 1)}
        >
          {t('opportunities.pagination.next')}
        </PaginationButton>
      </Box>
    </Box>
  );
}

function PaginationButton({
  children,
  onClick,
  disabled,
  ...rest
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  'data-testid'?: string;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={rest['data-testid']}
      sx={{
        height: 32,
        padding: '0 14px',
        background: tokens.color.surface,
        border: `1px solid ${tokens.color.line}`,
        borderRadius: '8px',
        color: tokens.color.ink,
        fontSize: 12.5,
        fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: 'inherit',
        opacity: disabled ? 0.5 : 1,
        '&:hover': { borderColor: disabled ? tokens.color.line : tokens.color.ink },
      }}
    >
      {children}
    </Box>
  );
}

// SW-148: computeInitials / formatLocation / hashIndex extraídos a `_helpers.ts`
// con tests unitarios dedicados. `OpportunitiesPage` también los consume.
